// What `@` can reach, and what a mention chip says once it is written.
//
// A mention is one token — `@[Label](prj_kf_auth)`, see lib/mentions.ts — but two
// jobs: the editor needs a list to autocomplete against, and the rendered chip needs
// the record's CURRENT name and somewhere to go when tapped. Both used to come
// straight out of the store, which quietly made every surface that shows prose a
// team-only surface.
//
// **That was a bug the moment a comment rendered on the client's page.** The row card
// is shared with the client's seat (see RowCardView), the seat mounts no store, and
// `useStore` throws rather than returning empty — so one team comment naming a
// record would have taken the client's whole page down.
//
// **And it is the reason a client could not use the editor at all.** A picker backed
// by the store would offer a client every org, project, person and deal the team
// has. Backed by a scope, it offers exactly what their own page already shows them:
// their people, their projects, their artifacts — data the server sent them, so
// nothing new is disclosed by making it typeable.
//
// Three shapes, then: the team's (everything), the client's (their page), and the
// default when nobody provides one (a chip that renders its label and goes nowhere).

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { titleField, type EntityKind } from '../lib/model'
import { useStore } from '../lib/store'

export interface MentionCandidate {
  id: string
  title: string
  kind: EntityKind
}

export interface MentionScope {
  /** What `@` offers, already filtered by the query and capped by the caller. */
  search: (query: string) => MentionCandidate[]
  /** The record's current name, or null when this scope cannot see it. A chip then
   *  falls back to the label frozen into the token, which is why the token carries
   *  one at all. */
  title: (id: string) => string | null
  /** Where a chip goes when tapped. Absent on the client's page: they have no record
   *  pages, and a chip that navigates nowhere should not pretend to be a link. */
  open?: ((id: string) => void) | undefined
}

const EMPTY: MentionScope = { search: () => [], title: () => null }

const Ctx = createContext<MentionScope>(EMPTY)

export function MentionProvider({ scope, children }: { scope: MentionScope; children: ReactNode }) {
  return <Ctx.Provider value={scope}>{children}</Ctx.Provider>
}

export function useMentionScope(): MentionScope {
  return useContext(Ctx)
}

/**
 * A scope over a flat list — the client's page, and anything else holding records as
 * plain rows rather than as a folded log.
 */
export function useListScope(
  rows: MentionCandidate[],
  open?: ((id: string) => void) | undefined,
): MentionScope {
  return useMemo(() => {
    const byId = new Map(rows.map((r) => [r.id, r.title]))
    return {
      search: (query: string) => {
        const q = query.trim().toLowerCase()
        return rows
          .filter((r) => r.title && (q === '' || r.title.toLowerCase().includes(q)))
          .sort((a, b) => a.title.localeCompare(b.title))
          .slice(0, 6)
      },
      title: (id: string) => byId.get(id) ?? null,
      open,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, open])
}

/**
 * The team's scope: the whole folded log, and record pages to go to.
 *
 * Mounted once inside the store provider rather than per surface, so a new screen
 * that renders prose gets working mentions without knowing this file exists.
 */
export function WorkspaceMentions({ children }: { children: ReactNode }) {
  const { state, version } = useStore()
  const navigate = useNavigate()
  const scope = useMemo<MentionScope>(
    () => ({
      search: (query: string) => {
        const q = query.trim().toLowerCase()
        return Object.values(state.entities)
          .filter((e) => !e.archived)
          .map((e) => ({
            id: e.id,
            kind: e.kind,
            title: String(e.fields[titleField(e.kind)] ?? ''),
          }))
          .filter((m) => m.title && (q === '' || m.title.toLowerCase().includes(q)))
          .sort((a, b) => a.title.localeCompare(b.title))
          .slice(0, 6)
      },
      title: (id: string) => {
        const rec = state.entities[id]
        if (!rec) return null
        const t = rec.fields[titleField(rec.kind)]
        return typeof t === 'string' && t.trim() ? t : null
      },
      open: (id: string) => navigate(`/e/${id}`),
    }),
    // `version` is the store's change counter: the fold is mutated in place, so
    // `state` alone never changes identity. See lib/store.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version, navigate],
  )
  return <MentionProvider scope={scope}>{children}</MentionProvider>
}
